// Shared embed helpers for the blog content editor.
//
// We deliberately DO NOT let authors paste arbitrary embed/iframe/script
// markup (that would widen the open stored-XSS surface — see the security
// audit). Instead an author pastes a URL, we match it against a fixed
// allowlist of oEmbed providers, and we store only { provider, url }. The
// publish script turns that into a native WordPress `core/embed` block, and
// WordPress's own oEmbed pipeline renders it on the live site.

export type EmbedProvider = 'youtube' | 'twitter' | 'instagram' | 'vimeo' | 'tiktok';

export interface EmbedProviderDef {
  slug: EmbedProvider;
  label: string;
  icon: string; // emoji shown on the placeholder card
  test: (u: URL) => boolean;
}

// The allowlist. Adding a provider here (and to the WP-slug map in the publish
// script) is all it takes to support a new one.
export const EMBED_PROVIDERS: EmbedProviderDef[] = [
  {
    slug: 'youtube',
    label: 'YouTube',
    icon: '▶',
    test: (u) => /(^|\.)youtube\.com$/i.test(u.hostname) || /^(www\.)?youtu\.be$/i.test(u.hostname),
  },
  {
    slug: 'twitter',
    label: 'X (Twitter)',
    icon: '𝕏',
    test: (u) => /(^|\.)(twitter|x)\.com$/i.test(u.hostname),
  },
  {
    slug: 'instagram',
    label: 'Instagram',
    icon: '◈',
    test: (u) => /(^|\.)instagram\.com$/i.test(u.hostname),
  },
  {
    slug: 'vimeo',
    label: 'Vimeo',
    icon: '▶',
    test: (u) => /(^|\.)vimeo\.com$/i.test(u.hostname),
  },
  {
    slug: 'tiktok',
    label: 'TikTok',
    icon: '♪',
    test: (u) => /(^|\.)tiktok\.com$/i.test(u.hostname),
  },
];

export const EMBED_CLASS = 'amada-embed';

/** The names of the providers, for user-facing prompts/help. */
export const EMBED_PROVIDER_LABELS = EMBED_PROVIDERS.map((p) => p.label).join(', ');

/** Match a raw URL against the allowlist. Returns the provider def or null. */
export function detectEmbedProvider(rawUrl: string): EmbedProviderDef | null {
  let u: URL;
  try {
    u = new URL(rawUrl.trim());
  } catch {
    return null;
  }
  if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
  return EMBED_PROVIDERS.find((p) => p.test(u)) ?? null;
}

/** Normalize a validated embed URL for storage: trim and force https. */
export function normalizeEmbedUrl(rawUrl: string): string {
  const trimmed = rawUrl.trim();
  try {
    const u = new URL(trimmed);
    if (u.protocol === 'http:') u.protocol = 'https:';
    return u.toString();
  } catch {
    return trimmed;
  }
}

export function providerBySlug(slug: string): EmbedProviderDef | null {
  const s = slug === 'x' ? 'twitter' : slug;
  return EMBED_PROVIDERS.find((p) => p.slug === s) ?? null;
}

// --- small HTML helpers (no external deps) --------------------------------

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function decodeEntities(s: string): string {
  return s
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');
}

/**
 * Inner markup for the embed placeholder card. Uses spans only (no nested
 * <div>), so the surrounding `<div class="amada-embed">…</div>` can be matched
 * with a simple non-greedy regex on both the preview and publish sides.
 */
export function embedCardInnerHtml(def: EmbedProviderDef, url: string): string {
  const safeUrl = escapeHtml(url);
  return (
    `<span class="amada-embed__icon" aria-hidden="true">${def.icon}</span>` +
    `<span class="amada-embed__body">` +
    `<span class="amada-embed__label">${escapeHtml(def.label)} embed</span>` +
    `<a class="amada-embed__url" href="${safeUrl}" target="_blank" rel="noopener noreferrer">${safeUrl}</a>` +
    `</span>`
  );
}

/** The full stored element for an embed (what Quill serializes). */
export function embedElementHtml(def: EmbedProviderDef, url: string): string {
  return (
    `<div class="${EMBED_CLASS}" data-embed-provider="${escapeHtml(def.slug)}" ` +
    `data-embed-url="${escapeHtml(url)}" contenteditable="false">` +
    embedCardInnerHtml(def, url) +
    `</div>`
  );
}

const EMBED_DIV_RX = new RegExp(
  `<div\\b[^>]*class="[^"]*\\b${EMBED_CLASS}\\b[^"]*"[^>]*>[\\s\\S]*?<\\/div>`,
  'gi',
);

/**
 * Replace stored embed elements with freshly-rendered, safe preview cards.
 * Used by the admin approval drawer, which renders blog HTML via
 * dangerouslySetInnerHTML. Only allowlisted providers render; the URL is
 * re-escaped, so nothing from the stored blob reaches the DOM unescaped.
 */
export function renderEmbedsForPreview(html: string): string {
  return html.replace(EMBED_DIV_RX, (block) => {
    const provider = (block.match(/data-embed-provider="([^"]*)"/i)?.[1] ?? '').toLowerCase();
    const url = decodeEntities(block.match(/data-embed-url="([^"]*)"/i)?.[1] ?? '');
    const def = providerBySlug(provider);
    if (!def || !url) return ''; // unknown provider / missing url — drop it
    return (
      `<div class="${EMBED_CLASS} amada-embed--preview" data-embed-provider="${escapeHtml(def.slug)}">` +
      embedCardInnerHtml(def, url) +
      `</div>`
    );
  });
}
